import dayjs, { Dayjs } from 'dayjs';
import type { IConfigurationVariable, IValueType } from '../../types';

type IRangeType = string | number | undefined;
type IExtendedValueType = IValueType | Dayjs;

export const getNumericValidationInfo = (variable: IConfigurationVariable, value: number) => {
  const isEqual = (a: IExtendedValueType, b: IExtendedValueType) => a === b;
  const isInRange = (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) =>
    lower !== undefined &&
    upper !== undefined &&
    currentValue !== null &&
    currentValue !== undefined &&
    lower <= currentValue &&
    upper >= currentValue;

  return getValidationInfo(variable, value, isEqual, isInRange, (v) => v as string);
};

export const getDateValidationInfo = (variable: IConfigurationVariable, value: Dayjs | null) => {
  const formatDate = (v: IValueType) => dayjs(v as string).format('MM/DD/YYYY');
  const isEqual = (a: IExtendedValueType, b: IExtendedValueType) =>
    dayjs(a as IRangeType).format() === dayjs(b as IRangeType).format();
  const isInRange = (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) =>
    (dayjs(lower).isBefore(currentValue as Dayjs) || isEqual(lower, currentValue)) &&
    (dayjs(upper).isAfter(currentValue as Dayjs) || isEqual(upper, currentValue));

  return getValidationInfo(variable, value, isEqual, isInRange, formatDate);
};

const getValidationInfo = (
  variable: IConfigurationVariable,
  value: IExtendedValueType,
  isEqual: (a: IExtendedValueType, b: IExtendedValueType) => boolean,
  isInRange: (lower: IRangeType, upper: IRangeType, currentValue: IExtendedValueType) => boolean,
  messageDelegate: (v: IValueType) => string
) => {
  //assume that unset value is always valid
  let valid = value === undefined || value === null;
  const validValues: string[] = [];
  if (!variable?.values) {
    return { valid, validValues };
  }
  for (const variableValue of variable.values) {
    if (Object.hasOwn(variableValue, 'name')) {
      if (isEqual(variableValue.value, value)) {
        valid = true;
      }
      validValues.push(`${messageDelegate(variableValue.value)}`);
    }

    if (!Object.hasOwn(variableValue, 'name')) {
      if (isInRange(variableValue.lower, variableValue.upper, value)) {
        valid = true;
      }
      validValues.push(
        `[${messageDelegate(variableValue.lower)} - ${messageDelegate(variableValue.upper)}]`
      );
    }
  }

  return { valid, validValues };
};

export const getRangeDate = (variable: IConfigurationVariable, propertyName: string) => {
  if (!variable.values?.length) {
    return dayjs(new Date(0, 1, 1));
  }

  let value = null;
  if (propertyName === 'lower') {
    value = variable.values[0];
  } else if (propertyName === 'upper') {
    value = variable.values.at(-1);
  }

  if (!value) {
    throw new Error(`unknown propertyName: ${propertyName}`);
  }

  return value[propertyName] ? dayjs(value[propertyName]) : dayjs(value.value as string);
};

export const fixTimezoneOffset = (dayjsDate: Dayjs | null) => {
  if (!dayjsDate) {
    return null;
  }

  const copy = dayjs(dayjsDate);
  const date = dayjsDate.toDate();
  const offset = date.getTimezoneOffset();
  return copy.add(offset, 'minute').toDate();
};

export const getDateOnly = (dayjsDate: Dayjs | null) => {
  if (!dayjsDate) {
    return null;
  }

  if (!dayjsDate.isValid()) {
    return dayjsDate;
  }

  const dateOnlyString = dayjsDate.format('YYYY-MM-DDT00:00:00[Z]');
  return dayjs(dateOnlyString);
};

export const toInputFormat = (isoDateString: string) => {
  const datePart = isoDateString.split('T')[0];
  const dateParts = datePart.split('-');
  return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
};
